const DEFAULT_FONT_SIZE_STEP = 2;
const DEFAULT_MIN_FONT_SIZE = 12;

let measureNode = null;

function getMeasureNode() {
  if (typeof document === 'undefined') {
    return null;
  }

  if (!measureNode) {
    measureNode = document.createElement('div');
    measureNode.setAttribute('aria-hidden', 'true');
    measureNode.style.position = 'absolute';
    measureNode.style.left = '-10000px';
    measureNode.style.top = '0';
    measureNode.style.visibility = 'hidden';
    measureNode.style.pointerEvents = 'none';
    measureNode.style.whiteSpace = 'normal';
    measureNode.style.wordBreak = 'normal';
    measureNode.style.overflowWrap = 'break-word';
    measureNode.style.margin = '0';
    measureNode.style.padding = '0';
    measureNode.style.border = '0';
    document.body.appendChild(measureNode);
  }

  return measureNode;
}

/**
 * Build a descending font-size sequence from startSize down to minSize (inclusive).
 */
export function buildFontSizeSteps(
  startSize,
  { minSize = DEFAULT_MIN_FONT_SIZE, step = DEFAULT_FONT_SIZE_STEP } = {},
) {
  const start = Math.round(startSize);
  const min = Math.round(minSize);
  const delta = Math.max(1, Math.round(step));

  if (start <= 0 || min <= 0 || start < min) {
    return start > 0 ? [start] : [];
  }

  const steps = [];

  for (let size = start; size >= min; size -= delta) {
    steps.push(size);
  }

  if (steps[steps.length - 1] !== min) {
    steps.push(min);
  }

  return steps;
}

function measureTextBounds(node, {
  text,
  fontFamily,
  fontSize,
  lineHeight,
  maxWidth,
  fontWeight = 'normal',
  letterSpacing,
  textAlign = 'left',
  textTransform,
}) {
  node.textContent = text;
  node.style.width = `${maxWidth}px`;
  node.style.maxWidth = `${maxWidth}px`;
  node.style.fontFamily = fontFamily;
  node.style.fontSize = `${fontSize}px`;
  node.style.lineHeight = String(lineHeight);
  node.style.fontWeight = fontWeight;
  node.style.letterSpacing = letterSpacing ?? '';
  node.style.textAlign = textAlign;
  node.style.textTransform = textTransform ?? '';

  return {
    width: Math.ceil(node.scrollWidth),
    height: Math.ceil(node.scrollHeight),
  };
}

function fitsWithinBounds({ width, height }, maxWidth, maxHeight) {
  return width <= maxWidth && height <= maxHeight;
}

/**
 * Find the largest font size at which headline text fits within max width/height.
 *
 * Measurement uses a hidden, off-screen DOM node so results match live export
 * rendering (dom-to-image-more). Does not truncate text; when nothing fits,
 * returns didFit: false so the caller can surface a warning.
 *
 * @param {object} options
 * @param {string} options.text - Headline copy to measure.
 * @param {string} options.fontFamily - CSS font-family value (e.g. "'DM Serif Display', serif").
 * @param {number} options.startFontSize - Initial font size in px to try first.
 * @param {number} options.maxWidth - Maximum rendered width in px.
 * @param {number} options.maxHeight - Maximum rendered height in px.
 * @param {number|string} options.lineHeight - CSS line-height for the headline.
 * @param {number} [options.minFontSize] - Smallest font size to try (default 12).
 * @param {number} [options.step] - Px decrement between attempts (default 2).
 * @param {number[]|null} [options.fontSizeSteps] - Explicit step sequence; overrides min/step.
 * @param {string} [options.fontWeight]
 * @param {string} [options.letterSpacing]
 * @param {string} [options.textAlign]
 * @param {string} [options.textTransform]
 * @returns {{ fontSize: number, didFit: boolean }}
 */
export function fitTextToBounds({
  text,
  fontFamily,
  startFontSize,
  maxWidth,
  maxHeight,
  lineHeight,
  minFontSize = DEFAULT_MIN_FONT_SIZE,
  step = DEFAULT_FONT_SIZE_STEP,
  fontSizeSteps = null,
  fontWeight,
  letterSpacing,
  textAlign,
  textTransform,
}) {
  const trimmed = (text ?? '').trim();
  const start = Math.round(startFontSize);

  if (!trimmed) {
    return { fontSize: start, didFit: true };
  }

  const node = getMeasureNode();

  if (!node) {
    return { fontSize: start, didFit: true };
  }

  const steps = fontSizeSteps ?? buildFontSizeSteps(start, { minSize: minFontSize, step });
  const measureOptions = {
    text: trimmed,
    fontFamily,
    lineHeight,
    maxWidth,
    fontWeight,
    letterSpacing,
    textAlign,
    textTransform,
  };

  let lastAttemptedSize = steps[steps.length - 1] ?? start;

  for (const fontSize of steps) {
    lastAttemptedSize = fontSize;
    const bounds = measureTextBounds(node, { ...measureOptions, fontSize });

    if (fitsWithinBounds(bounds, maxWidth, maxHeight)) {
      return { fontSize, didFit: true };
    }
  }

  return { fontSize: lastAttemptedSize, didFit: false };
}

/**
 * Measure rendered text bounds at a fixed font size (for layout budget calculations).
 *
 * @returns {{ width: number, height: number }}
 */
export function measureRenderedText({
  text,
  fontFamily,
  fontSize,
  lineHeight,
  maxWidth,
  fontWeight = 'normal',
  letterSpacing,
  textAlign = 'left',
  textTransform,
}) {
  const trimmed = (text ?? '').trim();
  const node = getMeasureNode();

  if (!trimmed || !node) {
    return { width: 0, height: 0 };
  }

  return measureTextBounds(node, {
    text: trimmed,
    fontFamily,
    fontSize,
    lineHeight,
    maxWidth,
    fontWeight,
    letterSpacing,
    textAlign,
    textTransform,
  });
}

export {
  DEFAULT_FONT_SIZE_STEP,
  DEFAULT_MIN_FONT_SIZE,
};
